import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Check, X } from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { listTeacherRoleRequestsForManagement, reopenTeacherRoleRequest, reviewTeacherRoleRequest, revokeTeacherRoleRequest, type TeacherRoleRequest } from '@/services/teacherAppAccessService'
import { useNavigate } from 'react-router-dom'
import { formatPedagogySystemTimestamp } from '@/lib/pedagogyTime'

const statusLabels: Record<TeacherRoleRequest['status'], string> = {
  pending: 'Ожидает решения',
  approved: 'Одобрена',
  rejected: 'Отклонена',
  cancelled: 'Отменена',
  revoked: 'Отозвана',
}

const statusFilters: Array<{ value: 'all' | TeacherRoleRequest['status']; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'pending', label: 'Ожидают решения' },
  { value: 'approved', label: 'Одобрены' },
  { value: 'rejected', label: 'Отклонены' },
  { value: 'cancelled', label: 'Отменены' },
  { value: 'revoked', label: 'Отозваны' },
]

export function PedagogyTeacherRoleRequestsPage() {
  const navigate = useNavigate()
  const { isAdvanced, isPedagogyLead } = usePedagogyAccess()
  const [requests, setRequests] = useState<TeacherRoleRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | TeacherRoleRequest['status']>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Загружаем весь список один раз, чтобы в фильтрах показывались реальные
      // количества заявок по каждому статусу.
      setRequests(await listTeacherRoleRequestsForManagement())
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить заявки')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const decide = async (requestId: string, decision: 'approved' | 'rejected') => {
    setBusyId(requestId)
    try {
      await reviewTeacherRoleRequest(requestId, decision)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сохранить решение')
    } finally {
      setBusyId(null)
    }
  }

  const revoke = async (requestId: string) => {
    setBusyId(requestId)
    try {
      await revokeTeacherRoleRequest(requestId)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось снять доступ')
    } finally {
      setBusyId(null)
    }
  }

  const reopen = async (requestId: string) => {
    setBusyId(requestId)
    try {
      await reopenTeacherRoleRequest(requestId)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось вернуть заявку на рассмотрение')
    } finally {
      setBusyId(null)
    }
  }

  const canDecide = isAdvanced || isPedagogyLead
  const canChangeApprovedAccess = canDecide
  const statusCounts = useMemo(() => requests.reduce<Record<string, number>>((counts, request) => {
    counts[request.status] = (counts[request.status] || 0) + 1
    return counts
  }, {}), [requests])
  const visibleRequests = statusFilter === 'all'
    ? requests
    : requests.filter(request => request.status === statusFilter)

  if (!canChangeApprovedAccess) {
    return <Card className="p-6 text-sm text-muted-foreground">Просматривать заявки могут сотрудники педагогического отдела.</Card>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button type="button" onClick={() => navigate(-1)} className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={16} /> Назад
          </button>
          <h1 className="text-xl font-semibold">Запросы на повышение до старшего педагога</h1>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card p-2">
        {statusFilters.map(filter => {
          const count = filter.value === 'all' ? requests.length : statusCounts[filter.value] || 0
          const active = statusFilter === filter.value
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => setStatusFilter(filter.value)}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'}`}
            >
              {filter.label} {count}
            </button>
          )
        })}
      </div>
      {error && <Card className="p-4 text-sm text-red-600">{error}</Card>}
      {loading ? (
        <Card className="p-6 text-sm text-muted-foreground">Загрузка…</Card>
      ) : visibleRequests.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">Заявок в этом статусе пока нет.</Card>
      ) : (
        <div className="space-y-3">
          {visibleRequests.map(request => {
            // A rejected history row stays visible, but cannot be reopened
            // while a newer pending or approved row exists.
            const hasActiveSibling = requests.some(other =>
              other.id !== request.id
              && other.teacher_id === request.teacher_id
              && other.partner_number === request.partner_number
              && (other.status === 'pending' || other.status === 'approved')
            )
            return (
            <Card key={request.id} className="p-3 sm:p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{request.teacher_name}</p>
                    <Badge text={statusLabels[request.status]} variant={request.status === 'approved' ? 'success' : request.status === 'pending' ? 'warning' : 'neutral'} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>{request.partner_name || `Франчайзи №${request.partner_number}`}</span>
                    <span>Запрошено {formatPedagogySystemTimestamp(request.requested_at)}</span>
                  </div>
                </div>
                {request.status === 'pending' && canDecide && (
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button size="sm" onClick={() => void decide(request.id, 'approved')} disabled={busyId !== null}>
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Check className="h-4 w-4" /> Одобрить</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => void decide(request.id, 'rejected')} disabled={busyId !== null}>
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><X className="h-4 w-4" /> Отклонить</span>
                    </Button>
                  </div>
                )}
                {request.status === 'approved' && canChangeApprovedAccess && (
                  <Button variant="outline" size="sm" onClick={() => void revoke(request.id)} disabled={busyId !== null}>
                    Снять доступ
                  </Button>
                )}
                {request.status === 'rejected' && canDecide && !hasActiveSibling && (
                  <Button variant="outline" size="sm" onClick={() => void reopen(request.id)} disabled={busyId !== null}>
                    Вернуть на рассмотрение
                  </Button>
                )}
                {request.status === 'rejected' && hasActiveSibling && (
                  <span className="text-xs text-muted-foreground">По этому педагогу уже есть актуальная заявка</span>
                )}
              </div>
            </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
